import { Close } from '@mui/icons-material';
import { Box, Button, IconButton, Popover, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import type { CalendarBooking } from '../types/calendar';

interface BookingPopoverProps {
    booking: CalendarBooking | null;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    onViewDetails: (bookingId: number) => void;
}

const getStatusLabel = (status: CalendarBooking['status']): string => {
    switch (status) {
        case 'booked':
            return 'Booked';
        case 'accommodationVerified':
            return 'Verified';
        case 'keysSecured':
            return 'Keys Secured';
        case 'hasReview':
            return 'Has Review';
        case 'complete':
            return 'Complete';
        case 'canceled':
            return 'Canceled';
        default:
            return status;
    }
};

const getPlatformLabel = (platform: CalendarBooking['platform']): string => {
    return platform === 'airbnb' ? 'AirBnB' : 'Direct';
};

const BookingPopover = ({
    booking,
    anchorEl,
    onClose,
    onViewDetails,
}: BookingPopoverProps) => {
    if (!booking) return null;

    const checkin = DateTime.fromISO(booking.checkin);
    const checkout = DateTime.fromISO(booking.checkout);
    const nights = checkout.diff(checkin, 'days').days;

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 0,
                        border: '3px solid #6366f1',
                        backgroundColor: '#1a1a2e',
                        minWidth: '280px',
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '2px solid #374151',
                    px: 2,
                    py: 1,
                    backgroundColor: '#0f0f23',
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontSize: '0.85rem',
                    }}
                >
                    Booking Details
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: '#9ca3af',
                        '&:hover': {
                            color: '#fff',
                        },
                    }}
                >
                    <Close fontSize="small" />
                </IconButton>
            </Box>
            <Box sx={{ p: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                    }}
                >
                    {booking.countryCode && (
                        <img
                            src={`/flags/${booking.countryCode.toLowerCase()}.png`}
                            alt={`${booking.countryCode} flag`}
                            style={{
                                width: 24,
                                height: 16,
                                objectFit: 'cover',
                            }}
                        />
                    )}
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: '1.1rem',
                        }}
                    >
                        {booking.guestName}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr',
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                        Check-in:
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        {checkin.toFormat('EEE, MMM d, yyyy')}
                    </Typography>

                    <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                        Check-out:
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        {checkout.toFormat('EEE, MMM d, yyyy')}
                    </Typography>

                    <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                        Duration:
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        {nights} night{nights !== 1 ? 's' : ''}
                    </Typography>

                    <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                        Guests:
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        {booking.adultCount} adult
                        {booking.adultCount !== 1 ? 's' : ''}
                        {booking.childCount > 0 &&
                            `, ${booking.childCount} child${booking.childCount !== 1 ? 'ren' : ''}`}
                    </Typography>

                    <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                        Platform:
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        {getPlatformLabel(booking.platform)}
                    </Typography>

                    <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                        Status:
                    </Typography>
                    <Box
                        sx={{
                            display: 'inline-block',
                            px: 1,
                            py: 0.25,
                            border: '2px solid #6366f1',
                            backgroundColor: 'rgba(99, 102, 241, 0.1)',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            color: '#c7d2fe',
                            width: 'fit-content',
                        }}
                    >
                        {getStatusLabel(booking.status)}
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => onViewDetails(booking.id)}
                    sx={{
                        borderRadius: 0,
                        border: '2px solid #6366f1',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                    }}
                >
                    View Full Details
                </Button>
            </Box>
        </Popover>
    );
};

export default BookingPopover;
